import React, { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { createBooking, getFlightStatus } from "../api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import {
  Users,
  MapPin,
  CheckCircle2,
  Loader2,
  Plane,
  Clock,
  Info,
} from "lucide-react";
import { toast } from "sonner";

const statusStyles = {
  on_time: { key: "statusOnTime", cls: "bg-emerald-100 text-emerald-700" },
  slight_delay: { key: "statusSlightDelay", cls: "bg-amber-100 text-amber-700" },
  delayed: { key: "statusDelayed", cls: "bg-red-100 text-red-700" },
};

const BookingModal = ({ open, onOpenChange, booking }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [flightNumber, setFlightNumber] = useState("");
  const [flight, setFlight] = useState(null);
  const [flightLoading, setFlightLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const reset = () => {
    setForm({ name: "", email: "", phone: "", notes: "" });
    setFlightNumber("");
    setFlight(null);
    setFlightLoading(false);
    setDone(null);
    setLoading(false);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const checkFlight = async () => {
    const code = flightNumber.trim();
    if (code.length < 3) {
      toast.error(t("flightInvalid"));
      return;
    }
    setFlightLoading(true);
    try {
      const res = await getFlightStatus({
        flight_number: code,
        date: booking?.arrivalDate || null,
      });
      setFlight(res);
    } catch (err) {
      if (err?.response?.status === 400) toast.error(t("flightInvalid"));
      else toast.error(t("flightError"));
    } finally {
      setFlightLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error(t("bookingRequired"));
      return;
    }
    setLoading(true);
    try {
      const res = await createBooking({
        trip_type: booking.tripType,
        origin: booking.origin,
        destination_id: booking.destinationId,
        hotel: booking.hotel || "",
        vehicle_tier: booking.tier,
        arrival_date: booking.arrivalDate,
        departure_date: booking.departureDate,
        passengers: booking.passengers,
        flight_number: flightNumber.trim(),
        customer_name: form.name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      });
      setDone(res);
      toast.success(t("bookingSuccess"));
    } catch (err) {
      toast.error(t("bookingError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {done ? (
          <div className="text-center py-4">
            <CheckCircle2 className="w-14 h-14 text-teal-500 mx-auto mb-3" />
            <h3 className="font-display font-extrabold text-xl text-[#0d3b43]">
              {t("bookingConfirmed")}
            </h3>
            <p className="text-sm text-slate-500 mt-2">
              {t("bookingRef")}{" "}
              <span className="font-mono font-semibold text-teal-700">
                {done.id.slice(0, 8).toUpperCase()}
              </span>
            </p>
            <div className="mt-4 bg-teal-50 rounded-xl p-4 text-left text-sm">
              <div className="flex justify-between py-1">
                <span className="text-slate-500">{t("total")}</span>
                <span className="font-bold text-[#0d3b43]">${done.price_usd} USD</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">{t("passengers")}</span>
                <span className="font-semibold">{done.passengers}</span>
              </div>
            </div>
            <Button
              onClick={() => handleClose(false)}
              className="mt-5 w-full bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-full"
            >
              {t("close")}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-[#0d3b43]">
                {t("completeBooking")}
              </DialogTitle>
              <DialogDescription className="flex flex-col gap-1 pt-1">
                <span className="flex items-center gap-1.5 text-teal-700 font-semibold">
                  <MapPin className="w-4 h-4" /> {booking?.summary}
                </span>
                <span className="flex items-center gap-1.5 text-slate-500">
                  <Users className="w-4 h-4" /> {booking?.passengers} {t("pax")} ·{" "}
                  <span className="font-bold text-[#0d3b43]">${booking?.price} USD</span>
                </span>
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-3 pt-2">
              <div>
                <Label htmlFor="bk-name">{t("fullName")}</Label>
                <Input
                  id="bk-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("fullNamePh")}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="bk-email">Email</Label>
                  <Input
                    id="bk-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="bk-phone">{t("phone")}</Label>
                  <Input
                    id="bk-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 555 000 0000"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="bk-notes">{t("notes")}</Label>
                <Textarea
                  id="bk-notes"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder={t("notesPh")}
                />
              </div>

              <div>
                <Label htmlFor="bk-flight" className="flex items-center gap-1.5">
                  <Plane className="w-3.5 h-3.5 text-teal-600 -rotate-12" />
                  {t("flightNumber")}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="bk-flight"
                    value={flightNumber}
                    onChange={(e) => {
                      setFlightNumber(e.target.value.toUpperCase());
                      setFlight(null);
                    }}
                    placeholder={t("flightNumberPh")}
                  />
                  <Button
                    type="button"
                    onClick={checkFlight}
                    disabled={flightLoading}
                    variant="outline"
                    className="shrink-0 border-teal-300 text-teal-700 hover:bg-teal-50 font-semibold"
                  >
                    {flightLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t("checkFlight")
                    )}
                  </Button>
                </div>

                {flight && (
                  <div className="mt-3 rounded-xl border border-teal-100 bg-teal-50/50 p-3.5 text-sm rc-fade-up">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-bold text-[#0d3b43]">
                        <Plane className="w-4 h-4 text-teal-600 -rotate-12" />
                        {flight.flight_number} · {flight.airline}
                      </span>
                      <span
                        className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                          statusStyles[flight.status]?.cls || ""
                        }`}
                      >
                        {t(statusStyles[flight.status]?.key || "statusOnTime")}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-3 text-slate-600">
                      <div>
                        <span className="block text-[11px] text-slate-400">
                          {t("scheduledArrival")}
                        </span>
                        <span className="font-semibold">{flight.scheduled_arrival}</span>
                      </div>
                      <div>
                        <span className="block text-[11px] text-slate-400">
                          {t("estimatedArrival")}
                        </span>
                        <span className="font-bold text-[#0d3b43]">
                          {flight.estimated_arrival}
                          {flight.delay_minutes > 0 && (
                            <span className="text-red-500 text-xs">
                              {" "}+{flight.delay_minutes}m
                            </span>
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[11px] text-slate-400">
                          {t("terminal")}
                        </span>
                        <span className="font-semibold">T{flight.terminal}</span>
                      </div>
                      <div>
                        <span className="block text-[11px] text-slate-400">
                          {t("pickupReady")}
                        </span>
                        <span className="font-semibold flex items-center gap-1 text-teal-700">
                          <Clock className="w-3.5 h-3.5" />
                          {flight.pickup_time}
                        </span>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-teal-800/80">{t("pickupNote")}</p>
                    <p className="mt-1.5 flex items-center gap-1 text-[10px] text-slate-400">
                      <Info className="w-3 h-3" />
                      {t("flightSimNote")}
                    </p>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-extrabold rounded-full"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t("confirmBooking")}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
